import { css } from '@emotion/react';

import { breakpoints, typography2 as typography } from '@coursera/cds-core';

// Shared visually-hidden pattern used for all SR-only live regions and descriptions.
// Interpolated as a plain string into the parent css template literal.
const srOnly = `
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip-path: inset(50%);
  white-space: nowrap;
`;

export const classes = {
  root: 'cds-ChatResponseField-root',
  focusRing: 'cds-ChatResponseField-focusRing',
  contextRow: 'cds-ChatResponseField-contextRow',
  textRow: 'cds-ChatResponseField-textRow',
  leadingAction: 'cds-ChatResponseField-leadingAction',
  textarea: 'cds-ChatResponseField-textarea',
  actionRow: 'cds-ChatResponseField-actionRow',
  actionRowRight: 'cds-ChatResponseField-actionRowRight',
  charLimit: 'cds-ChatResponseField-charLimit',
  charLimitError: 'cds-ChatResponseField-charLimitError',
  charLimitHidden: 'cds-ChatResponseField-charLimitHidden',
  charLimitAnnouncement: 'cds-ChatResponseField-charLimitAnnouncement',
  dragAnnouncement: 'cds-ChatResponseField-dragAnnouncement',
  streamingAnnouncement: 'cds-ChatResponseField-streamingAnnouncement',
  maxLengthDescription: 'cds-ChatResponseField-maxLengthDescription',
  micAnnouncement: 'cds-ChatResponseField-micAnnouncement',
  sendButton: 'cds-ChatResponseField-sendButton',
  multiLine: 'cds-ChatResponseField-multiLine',
  dragging: 'cds-ChatResponseField-dragging',
  trailingActionsWrapper: 'cds-ChatResponseField-trailingActionsWrapper',
  streaming: 'cds-ChatResponseField-streaming',
  hiddenFileInput: 'cds-ChatResponseField-hiddenFileInput',
  textMeasure: 'cds-ChatResponseField-textMeasure',
};

// Matches the body-primary line-height baked into the textarea font style.
export const LINE_HEIGHT_PX = 24;
// Total vertical padding (top + bottom) of the textarea element.
// The textarea uses padding: var(--cds-spacing-100) on all sides, so both
// top and bottom contribute — 8px + 8px = 16px.
export const TEXTAREA_VERTICAL_PADDING_PX = 16;
// Default maximum number of visible rows before the textarea starts scrolling.
export const DEFAULT_MAX_ROWS = 7;

export const SINGLE_LINE_HEIGHT = LINE_HEIGHT_PX + TEXTAREA_VERTICAL_PADDING_PX;
// Used as the CSS fallback when the --crf-max-textarea-height variable is not set.
export const MAX_TEXTAREA_HEIGHT = LINE_HEIGHT_PX * DEFAULT_MAX_ROWS + TEXTAREA_VERTICAL_PADDING_PX;

const getChatResponseFieldCss = css`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: var(--cds-spacing-100);
  padding: var(--cds-spacing-100);
  background: var(--cds-color-emphasis-neutral-background-xweak);
  border-radius: var(--cds-border-radius-200);

  .${classes.focusRing} {
    display: none;
    position: absolute;
    inset: -4px;

    /* outer ring: container border-radius (--cds-border-radius-200 = 8px) + 3px outset */
    border-radius: 11px;
    border: 1.5px solid var(--cds-color-interactive-stroke-primary-focus-invert);
    pointer-events: none;

    &::after {
      content: '';
      display: block;
      position: absolute;
      inset: 2px;

      /* inner ring: container border-radius (--cds-border-radius-200 = 8px) + 1px inset */
      border-radius: 9px;
      border: 1.5px solid var(--cds-color-interactive-stroke-primary-focus);
    }
  }

  &:focus-within .${classes.focusRing} {
    display: block;
  }

  .${classes.contextRow} {
    display: flex;
    flex-wrap: wrap;
    gap: var(--cds-spacing-100);
    align-items: center;
  }

  .${classes.textRow} {
    display: flex;
    align-items: center;
    min-width: 0;
  }

  .${classes.leadingAction} {
    flex-shrink: 0;
    display: flex;
    align-items: center;
    gap: var(--cds-spacing-100);
  }

  .${classes.hiddenFileInput} {
    display: none;
  }

  .${classes.textMeasure} {
    position: absolute;
    top: 0;
    left: 0;
    width: 0;
    overflow: hidden;
    visibility: hidden;
    pointer-events: none;
    white-space: pre;
    ${typography.bodyPrimary};
    padding: 0 var(--cds-spacing-100);
  }

  .${classes.textarea} {
    flex: 1 1 auto;
    min-width: 0;
    background: transparent;
    border: none;
    outline: none;
    resize: none;
    ${typography.bodyPrimary};
    color: var(--cds-color-neutral-primary);
    padding: var(--cds-spacing-100);
    height: ${SINGLE_LINE_HEIGHT}px;
    max-height: var(--crf-max-textarea-height, ${MAX_TEXTAREA_HEIGHT}px);
    overflow-y: auto;

    &::placeholder {
      color: var(--cds-color-neutral-primary-weak);
    }

    scrollbar-width: thin;
    scrollbar-color: var(--cds-color-neutral-stroke-primary-weak) transparent;

    &::-webkit-scrollbar {
      width: 12px;
    }

    &::-webkit-scrollbar-track {
      background: transparent;
    }

    &::-webkit-scrollbar-thumb {
      background: var(--cds-color-neutral-stroke-primary-weak);
      border-radius: var(--cds-border-radius-200);
      border: 2px solid transparent;
      background-clip: padding-box;
    }
  }

  .${classes.actionRow} {
    flex-shrink: 0;
    display: flex;
    align-items: center;
    gap: var(--cds-spacing-100);
  }

  .${classes.actionRowRight} {
    flex: 1 0 0;
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: var(--cds-spacing-100);
    min-width: 0;
  }

  /* Always in the DOM so the live region is registered on mount; hidden when unused. */
  .${classes.charLimitHidden},
    .${classes.charLimitAnnouncement},
    .${classes.dragAnnouncement},
    .${classes.streamingAnnouncement},
    .${classes.maxLengthDescription},
    .${classes.micAnnouncement} {
    ${srOnly}
  }

  .${classes.charLimit} {
    ${typography.bodySecondary};
    color: var(--cds-color-neutral-primary-weak);
    text-align: right;
    flex: 1 0 0;
    min-width: 0;

    &.${classes.charLimitError} {
      color: var(--cds-color-feedback-error);
    }
  }

  .${classes.trailingActionsWrapper} {
    display: contents;
  }

  &.${classes.streaming} .${classes.trailingActionsWrapper} {
    pointer-events: none;
    opacity: 0.4;
    display: flex;
    align-items: center;
  }

  &.${classes.dragging} {
    background: var(--cds-color-emphasis-neutral-background-weak);
    outline: 2px dashed var(--cds-color-interactive-stroke-primary-focus);
    outline-offset: -2px;
  }

  &.${classes.multiLine} {
    .${classes.textRow} {
      align-items: flex-start;
    }

    .${classes.textarea} {
      width: 100%;
    }

    .${classes.actionRow} {
      width: 100%;
      justify-content: space-between;
    }

    ${breakpoints.down('sm')} {
      .${classes.actionRow} {
        flex-direction: column;
        align-items: stretch;
      }

      .${classes.leadingAction} {
        min-width: 0;
        flex-shrink: 1;
        flex-wrap: wrap;
      }
    }
  }
`;

export default getChatResponseFieldCss;
